import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ContentLayout } from "@/components/common/content-layout";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { BundleDiscrepancyTable } from "@/features/audit/ui/bundle-discrepancy-table";
import { BundleDiscrepancyFilters } from "@/features/audit/ui/bundle-discrepancy-filters";
import { useBundleDiscrepancies } from "@/features/audit/hooks/use-bundle-discrepancies";
import { UserDetailsDrawer } from "@/features/users/ui/user-details-drawer";
import { AlertTriangle } from "lucide-react";

export default function BundleDiscrepancyAudit() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || 0);
  const bundle_id = searchParams.get("bundle_id") || undefined;
  const date_from = searchParams.get("date_from") || undefined;
  const date_to = searchParams.get("date_to") || undefined;
  const flag = searchParams.get("flag") || undefined;
  const user_search = searchParams.get("user_search") || undefined;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data, isLoading, isFetching, error } = useBundleDiscrepancies({
    page,
    bundle_id,
    date_from,
    date_to,
    flag,
    user_search,
  });

  const discrepancies = data?.discrepancies ?? [];
  const currentPage = data?.page ?? 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handleUserClick = (id: string) => {
    setSelectedUserId(id);
    setIsDrawerOpen(true);
  };

  return (
    <>
      <ContentLayout
        header={
          <>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Bundle Discrepancy
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Bundle usages with mismatched session counts or incorrect
                statuses.
              </p>
            </div>
            <BundleDiscrepancyFilters />
          </>
        }
        data={discrepancies}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage="Failed to load bundle discrepancies"
        emptyState={{
          message: "No discrepancies found",
          icon: <AlertTriangle className="h-8 w-8 text-muted-foreground" />,
        }}
      >
        <BundleDiscrepancyTable
          discrepancies={discrepancies}
          onUserClick={handleUserClick}
        />

        {discrepancies.length > 0 && (
          <div className="mt-4">
            <CustomPaginationComponent
              currentPage={currentPage}
              lastPage={lastPage}
              onPageChange={(newPage) => {
                const params = new URLSearchParams(searchParams);
                params.set("page", newPage.toString());
                setSearchParams(params);
              }}
            />
          </div>
        )}
      </ContentLayout>

      <UserDetailsDrawer
        userId={selectedUserId}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />
    </>
  );
}
